import { clearToken, refreshToken, setIsLoading, updateMeInfo } from 'store/slice/auth-slice';
import baseApi, { API_V1, customBaseQuery } from './base';
import { setFormClean } from 'store/reducers/formslice';
import { resetUploadState } from 'store/slice/design/uploadSlice';
import { ApplicationVersion, LoginTypes } from 'types/auth';
import { isInitiator, notifyAuthChange, setIsInitiator } from 'utils/authUtils';
import { ClearUserSettingFromSession } from 'utils/user-settings';

const LOGIN_BASE_URL = `${API_V1}/Login`;
const USER_BASE_URL = `${API_V1}/User`;

const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation({
      query: ({ username, password }) => ({
        url: LOGIN_BASE_URL,
        method: 'POST',
        data: { username, password },
        noAuth: true
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data?.userLoginModels) {
            localStorage.removeItem('isLoggedOut');
          }
        } catch (e) {
          console.log(e);
        } finally {
        }
      }
    }),

    verifyPassword: build.mutation({
      query: ({ username, password }) => ({
        url: `${LOGIN_BASE_URL}/Verify`,
        method: 'POST',
        data: { username, password },
        noAuth: true
      })
    }),

    resetPassword: build.mutation({
      query: ({ oldPassword, newPassword, resetToken }) => ({
        url: `${LOGIN_BASE_URL}/ResetPassword`,
        method: 'PUT',
        data: {
          oldPassword,
          newPassword,
          resetPasswordToken: resetToken
        },
        noAuth: true
      })
    }),

    forgotPassword: build.mutation({
      query: ({ emailId }) => ({
        url: `${LOGIN_BASE_URL}/ForgotPassword`,
        method: 'POST',
        data: {
          emailId
        },
        noAuth: true
      })
    }),

    refreshAuthToken: build.mutation<any, void>({
      queryFn: (_, api, extraOptions) => {
        return customBaseQuery(
          {
            url: `${LOGIN_BASE_URL}/Refresh`,
            method: 'PUT',
            data: {
              refreshToken: ''
            }
          },
          api,
          extraOptions
        );
      },
      onQueryStarted: async (_, api) => {
        try {
          const res = await api.queryFulfilled;
          if (res.data?.token) {
            setIsInitiator(!isInitiator());
            api.dispatch(refreshToken({ authToken: res.data?.token, refreshToken: res.data?.refreshtoken }));
            notifyAuthChange('refresh');
          }
        } catch (e) {}
      }
    }),

    me: build.query<any, void>({
      query: () => ({
        url: `${USER_BASE_URL}/me`
      }),
      transformResponse: (responseData: { data: any[] }) => responseData,
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          dispatch?.(setIsLoading(false));
          const { data } = await queryFulfilled;
          dispatch?.(updateMeInfo({ decodedAuthToken: data }));
        } catch (error) {
        } finally {
          dispatch?.(setIsLoading(true));
        }
      }
    }),

    logout: build.mutation<any, void>({
      query: () => ({
        url: `${LOGIN_BASE_URL}/Logout`,
        method: 'DELETE'
      }),
      onQueryStarted: async (_, api) => {
        try {
          localStorage.setItem('isLoggedOut', 'true');
          ClearUserSettingFromSession(true); // clearing user session data;
          await api.queryFulfilled;
          setIsInitiator(!isInitiator());
          notifyAuthChange('logout');
        } catch (e) {
        } finally {
          // clear token for both success and error during logout
          // so that user will be taken to login page.
          api.dispatch(setFormClean()); // To disable page blocker while session is expired
          api.dispatch(clearToken());
          api.dispatch(resetUploadState());
          document.cookie = '';
        }
      }
    }),
    getApplicationVersion: build.query<ApplicationVersion, void>({
      query: () => ({
        url: `${LOGIN_BASE_URL}/Version`
      })
    }),
    getLoginType: build.query<LoginTypes, void>({
      query: () => ({
        url: `${LOGIN_BASE_URL}/getloginType`
      })
    })
  })
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useResetPasswordMutation,
  useForgotPasswordMutation,
  useRefreshAuthTokenMutation,
  useVerifyPasswordMutation,
  useLazyGetApplicationVersionQuery,
  useMeQuery,
  useLazyMeQuery,
  useLazyGetLoginTypeQuery,
  useGetLoginTypeQuery
} = authApi;

export default authApi;
